/**
 * Utility functions for the notice-draft-wizard superseding notice display.
 */

/**
 * Extracts the tax year from an outcome breakdown category string.
 * The category is stored as `{taxYear}{taxAreaId}` e.g.
 * "2025taxareabd6cbe56-dbb0-4001-9e3a-310fabdde301".
 * We extract only the leading 4-digit year.
 */
export function extractYearFromCategory(category: string): string {
  const match = category.match(/^(\d{4})/);
  return match ? match[1] : category;
}

/**
 * Returns the quarter number (1–4) from a UTC month index (0–11).
 */
export function getQuarter(monthIndex: number): number {
  return Math.floor(monthIndex / 3) + 1;
}

/**
 * Formats a period cell value from an outcome breakdown.
 * Combines the year extracted from `category` with the quarter and short date
 * derived from `dueDate` (Unix timestamp in seconds).
 *
 * Example: category="2025taxareabd6cbe56-…", dueDate=1741608000
 *   → "Q1 2025 (Mar 10)"
 *
 * Falls back to just the year (or raw category) when dueDate is absent/invalid.
 */
export function formatPeriodCell(
  category: string,
  dueDate: number | string | undefined,
): string {
  const year = extractYearFromCategory(category);

  if (!dueDate) return year || category;

  const ts = typeof dueDate === "string" ? Number.parseFloat(dueDate) : dueDate;
  if (Number.isNaN(ts)) return year || category;

  const date = new Date(ts * 1000);
  const quarter = getQuarter(date.getUTCMonth());
  const shortDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

  return `Q${quarter} ${year} (${shortDate})`;
}

/**
 * Formats a Unix timestamp (seconds, possibly fractional) into a
 * human-readable date string e.g. "Apr 28, 2026".
 * Returns an empty string for absent or invalid values.
 */
export function formatNoticeDate(
  createdAt: number | string | undefined,
): string {
  if (!createdAt) return "";
  const ts =
    typeof createdAt === "string" ? Number.parseFloat(createdAt) : createdAt;
  if (Number.isNaN(ts)) return "";
  return new Date(ts * 1000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Converts a dueDate value to a comparable number for sorting.
 * - If it's already a number (Unix seconds), returns it directly.
 * - If it's a "YYYY/MM/DD" or "YYYY-MM-DD" string, parses it to a timestamp.
 * - Returns Infinity for absent/invalid values so they sort to the end.
 */
export function dueDateToSortKey(dueDate: number | string | undefined): number {
  if (!dueDate) return Number.POSITIVE_INFINITY;
  if (typeof dueDate === "number") return dueDate;
  // DatePicker format "YYYY/MM/DD" or ISO "YYYY-MM-DD"
  const normalized = dueDate.replace(/\//g, "-");
  const ts = Date.parse(normalized);
  return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts / 1000;
}

/**
 * Converts a Unix timestamp (seconds) to a "YYYY/MM/DD" string (UTC)
 * so it can be compared against DatePicker dueDate strings from new breakdowns.
 */
export function unixToDatePickerString(ts: number): string {
  const d = new Date(ts * 1000);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}/${mm}/${dd}`;
}
